/**
 * Custom hook for authentication actions
 */

import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { sendOTP, signIn, signOut } from '../api/auth';
import { createDeviceContext } from '../api/auth';
import { refreshTokenService } from '../services/refreshTokenService';
import { getStoredTokens } from '../utils/storage';
import { AuthTokens } from '../types/auth';
import { AuthStateActions } from './useAuthState';
import { analyticsClient } from 'sdks/analytics';

export interface AuthActions {
  login: (phoneNumber: string) => Promise<void>;
  verifyOtp: (otp: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  setNeedsOnboarding: (needsOnboarding: boolean) => void;
}

export const useAuthActions = (
  actions: AuthStateActions
): AuthActions => {
  const router = useRouter();

  const login = useCallback(async (phoneNumber: string) => {
    actions.setLoading(true);
    actions.setError(null);

    try {
      const request = {
        phone_number: `+91${phoneNumber}`,
        login_mechanism: 1,
        client_id: "influencer-app",
        device_context: createDeviceContext(),
      };

      const response = await sendOTP(request);

      // Store the user_id and session_info from the response for OTP verification
      localStorage.setItem('user_id', response.user_id);
      localStorage.setItem('session_info', response.session_info);
      analyticsClient.setSessionId(response.session_info);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      actions.setError(errorMessage);
      throw error;
    } finally {
      actions.setLoading(false);
    }
  }, [actions]);

  const verifyOtp = useCallback(async (otp: string) => {
    actions.setLoading(true);
    actions.setError(null);

    try {
      // Get user_id and session_info from localStorage (set during login)
      const userId = localStorage.getItem('user_id');
      const sessionInfo = localStorage.getItem('session_info');

      if (!userId || !sessionInfo) {
        throw new Error('Missing user session information');
      }

      const request = {
        user_id: userId,
        session_info: sessionInfo,
        otp: otp,
        client_id: "influencer-app",
      };

      const response = await signIn(request);


      // Store tokens with all required fields
      const tokens: AuthTokens = {
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        user_id: userId, // This is the user_id from sendOTP response
        is_new_user: response.is_new_user,
      };

      localStorage.setItem('access_token', tokens.access_token);
      localStorage.setItem('refresh_token', tokens.refresh_token);
      localStorage.setItem('user_id', tokens.user_id);
      localStorage.setItem('is_new_user', tokens.is_new_user?.toString() || 'false');

      actions.setUser(tokens);
      actions.setAuthenticated(true);
      actions.setError(null);

      // Set onboarding status based on is_new_user flag
      const needsOnboarding = response.is_new_user || false;
      actions.setNeedsOnboarding(needsOnboarding);

      // Redirect based on onboarding status
      if (needsOnboarding) {
        router.replace('/onboarding/personal-details');
      } else {
        router.replace('/home');
      }

    } catch (error) {
      let errorMessage = 'OTP verification failed';

      // Handle new error format
      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = error.message as string;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      actions.setError(errorMessage);
      throw error;
    } finally {
      actions.setLoading(false);
    }
  }, [actions, router]);

  const logout = useCallback(async () => {

    try {
      // Get stored tokens for signOut API call
      const storedTokens = getStoredTokens();

      if (storedTokens) {
        // Call signOut API to invalidate tokens on server
        await signOut({
          user_id: storedTokens.user_id,
          refresh_token: storedTokens.refresh_token,
          signout_type: 2,
          access_token: storedTokens.access_token,
          client_id: "influencer-app",
        });

      }
    } catch (error) {
      // Even if signOut API fails, we should still clear local state
    }

    // Clear tokens from storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('is_new_user');
    localStorage.removeItem('session_info');

    // Stop all refresh attempts and clear tokens
    refreshTokenService.stopAllRefreshAttempts();
    refreshTokenService.forceLogout();

    // Clear auth state
    actions.clearAuth();

    // Navigate to home page
    router.push('/');

  }, [actions, router]);

  const refreshAuth = useCallback(async () => {
    try {
      const result = await refreshTokenService.refreshTokens();

      if (result.success) {
        // Get updated tokens using storage utility
        const storedTokens = getStoredTokens();

        if (storedTokens) {
          actions.setUser(storedTokens);
          actions.setAuthenticated(true);
        }
      } else {

        // If refresh token expired or other critical error, logout
        if (result.error !== "") {
          logout();
        }
      }
    } catch (error) {
      logout();
    }
  }, [actions, logout]);

  return useMemo(() => ({
    login,
    verifyOtp,
    logout,
    refreshAuth,
    setNeedsOnboarding: actions.setNeedsOnboarding,
  }), [login, verifyOtp, logout, refreshAuth, actions.setNeedsOnboarding]);
};
